import type { ShellAuth } from '@thecvlb/auth-web';

import { RootState, selectUser, store } from 'store';
import { setToken } from 'store/user/userSlice';

import { authorizationHeader, getLoginService } from './loginService';
import { ownsSession, renewAccessTokenQuietly, sessionKeyOf } from './services';

/**
 * Credentials for code outside the RTK Query layer (LMD-682): callers ask for a token when they need
 * one. Covers portal-signed sessions (refresh token in the store) and login-service sessions (SDK).
 */

const state = () => store.getState() as RootState;
const user = () => selectUser(state());

export const getAccessToken = () => user()?.accessToken ?? '';

/** The `Authorization` value for a token of the current session, in the format its source takes. */
export const authorizationForSession = (token: string) =>
  authorizationHeader(token, user()?.authSource);

/**
 * An opaque key for the signed-in session. Capture it before an await and pass it to
 * `isCurrentSession` after, so a result is never delivered to a patient who replaced the one that
 * asked for it.
 */
export const selectSessionKey = (s: RootState) => sessionKeyOf(selectUser(s), s.signUp?.user);

export const getSessionKey = () => selectSessionKey(state());

/**
 * What session-scoped effects (sockets, the app's first data load) key on: the login-service
 * session, which a token renewal does not change, else the token as before (portal-signed sessions).
 */
export const selectSessionScope = (s: RootState) => {
  const user = selectUser(s);
  return user?.authSessionKey || user?.accessToken || '';
};

/** Whether the session a key was captured from is still signed in, across token renewals. */
export const isCurrentSession = (key: string) => ownsSession(key, getSessionKey());

/**
 * Renews without ever signing the patient out; resolves to the new access token, or null.
 *
 * Pass the key captured when the work started: if that session is gone (before or after renewal)
 * this resolves null, so a request is never replayed as another patient. `stillWanted` is the
 * caller's veto before anything is stored, e.g. an unmounted component.
 */
export const renewAccessToken = async (
  sessionKey = getSessionKey(),
  stillWanted: () => boolean = () => true
): Promise<string | null> => {
  const { accessToken, authSessionKey, refreshToken } = user() ?? {};
  const loginService = getLoginService();

  if (loginService && authSessionKey) {
    return currentLoginServiceToken(loginService, sessionKey, stillWanted, accessToken ?? '');
  }

  if (!refreshToken || !isCurrentSession(sessionKey)) {
    return null;
  }

  const renewed = await renewAccessTokenQuietly(refreshToken, accessToken);

  if (!renewed || !stillWanted()) {
    return null;
  }

  // The live key may already be the RETURNED token: another caller sharing this renewal can store
  // the pair first, and that is the same session rather than a replacement.
  const sameSession = isCurrentSession(sessionKey) || getSessionKey() === renewed.refreshToken;

  if (!sameSession) {
    return null;
  }

  store.dispatch(setToken(renewed));

  return renewed.accessToken;
};

/**
 * The SDK's current token (it renews near expiry; the bridge stores renewals). Null when nothing
 * newer is worth replaying with.
 */
const currentLoginServiceToken = async (
  loginService: ShellAuth,
  sessionKey: string,
  stillWanted: () => boolean,
  previous: string
): Promise<string | null> => {
  if (!isCurrentSession(sessionKey)) {
    return null;
  }

  const token = await loginService.getToken().catch(() => null);

  if (!token || token === previous || !stillWanted() || !isCurrentSession(sessionKey)) {
    return null;
  }

  return token;
};

const withAuth = (init: RequestInit, token: string): RequestInit => {
  const headers = new Headers(init.headers);

  if (token) {
    headers.set('Authorization', authorizationHeader(token, user()?.authSource));
  }

  return { credentials: 'include', ...init, headers };
};

export class SessionChangedError extends Error {
  constructor() {
    super('Session changed before the response was delivered');
  }
}

/**
 * `fetch` with the patient's token attached, renewing once and replaying after a 401. Rejects with
 * `SessionChangedError` when another patient signed in while the request was in flight, because the
 * response belongs to the patient who has gone.
 */
export const authorizedFetch = async (input: string, init: RequestInit = {}) => {
  const sessionKey = getSessionKey();
  const stillOwned = () => !sessionKey || isCurrentSession(sessionKey);
  const loginService = getLoginService();

  // Login-service session: `auth.fetch` attaches the token, renews once, never replays as someone
  // else. The store's token is the fallback for an origin the SDK doesn't trust.
  if (loginService && user()?.authSessionKey) {
    const response = await loginService.fetch(input, withAuth(init, getAccessToken()));

    if (!stillOwned()) {
      throw new SessionChangedError();
    }

    return response;
  }

  let response = await fetch(input, withAuth(init, getAccessToken()));

  if (response.status === 401 && sessionKey) {
    const renewed = await renewAccessToken(sessionKey);

    if (renewed) {
      response = await fetch(input, withAuth(init, renewed));
    }
  }

  if (!stillOwned()) {
    throw new SessionChangedError();
  }

  return response;
};
